import { ChangeEvent, Dispatch, useState } from 'react';
import type { DateInputChangeEvent, DateInputValue } from 'nhsuk-react-components';
import {
  Row, Col, DateInput, TextInput,
} from 'nhsuk-react-components';
import { RuleEditor } from 'schemas/IterationRule.schema';
import { RuleActions } from 'reducers/rule/ruleReducer';
import {
  getWidth, isValidInput, getRuleEditorControl, getRuleComparatorVariables,
  updateRuleComparator,
  isValidDate,
} from 'utils/ruleEditorUtils';
import { ErrorsDisplay } from '../ErrorsDisplay';

export interface TextAndDateEditorProps {
  ruleEditor?: RuleEditor,
  dispatch: Dispatch<RuleActions>;
  value: string;
  errors: Record<string, string>;
}

export function TextAndDateEditor({
  ruleEditor,
  dispatch,
  value,
  errors,
}: TextAndDateEditorProps) {
  const [showValidationError, setShowValidationError] = useState(false);

  const { label: dateLabel } = getRuleEditorControl(ruleEditor, 'DATE');

  const values = getRuleComparatorVariables(value, ruleEditor?.ComparatorTemplate);

  const [textValue, setTextValue] = useState(values[0] || '');
  const [dateValue, setDateValue] = useState(() => {
    if (values[1]) {
      const year = values[1].slice(0, 4);
      const month = values[1].slice(4, 6);
      const day = values[1].slice(6, 8);
      return {
        day: day || '',
        month: month || '',
        year: year || '',
      };
    }
    return { day: '', month: '', year: '' };
  });

  const {
    label, errorMessage, regEx, placeholderText, helperText, width,
  } = getRuleEditorControl(ruleEditor, 'TEXT');

  const sendValues = (newTextValue: string, newDateValue: { day: string; month: string; year: string }) => {
    const dateString = `${newDateValue.year}${newDateValue.month.padStart(2, '0')}${newDateValue.day.padStart(2, '0')}`;
    const formattedDateString = `${newDateValue.day.padStart(2, '0')}/${newDateValue.month.padStart(2, '0')}/${newDateValue.year}`;

    const isValid = isValidDate(newDateValue.day, newDateValue.month, newDateValue.year);
    if (isValid) {
      updateRuleComparator(dispatch, ruleEditor, newTextValue, [newTextValue, dateString], [newTextValue, formattedDateString]);
      return;
    }

    updateRuleComparator(dispatch, ruleEditor, '', [], []);
  };

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShowValidationError(false);
    const inputValue = e.target.value;

    let validateInput = true;

    if (inputValue !== '') {
      validateInput = isValidInput(inputValue, regEx);
      if (!validateInput) {
        setShowValidationError(true);
      }
    }

    if (validateInput) {
      setTextValue(inputValue);
      sendValues(inputValue, dateValue);
    }
  };

  const handleDateChange = (e: DateInputChangeEvent) => {
    const { day = '', month = '', year = '' } = (e.target.value || {}) as Partial<DateInputValue>;
    const newDateValue = { day, month, year };
    setDateValue(newDateValue);
    sendValues(textValue, newDateValue);
  };

  const getErrorMessage = () => {
    if (showValidationError) {
      return errorMessage;
    }

    return undefined;
  };

  return (
    <>
      <Row>
        <Col width="full">
          <TextInput
            id="rule-comparator"
            data-testid="rule-comparator"
            label={label}
            name="Comparator"
            onChange={handleTextChange}
            value={textValue}
            error={getErrorMessage()}
            placeholder={placeholderText}
            hint={!getErrorMessage() ? helperText : undefined}
            width={getWidth(width)}
          />
        </Col>
      </Row>
      <Row>
        <Col width="full">
          <DateInput
            value={dateValue}
            legend={dateLabel}
            onChange={handleDateChange}
            data-testid="rule-comparator-date"
          />
        </Col>
      </Row>
      <ErrorsDisplay errors={errors} errorKeys={['Comparator', 'AttributeName']} />
    </>
  );
}
